'use client'

import * as React from 'react'
import { cn } from '../../helpers/index.js'
import Picture from '../../components/picture.js'
import type { Img } from '../../types/props.js'
import { Heading, Text } from '../../index.js'
import { withLayout } from '../../shared/Styles.js'

export type FeaturedBlogPostsSemanticName =
  | 'root'
  | 'header'
  | 'title'
  | 'subtitle'
  | 'grid'
  | 'card'
  | 'cardImage'
  | 'cardContent'
  | 'cardTitle'
  | 'cardDescription'
  | 'cardMeta'
  | 'separator'

/**
 * 卡片宽度比例
 */
export type FeaturedBlogPostSpan = 'full' | '2/3' | '1/3'

/**
 * 博文卡片项数据接口
 */
export interface FeaturedBlogPostItem {
  /** 封面图片 */
  image: {
    mobile?: Img
    pad?: Img
    laptop?: Img
    desktop?: Img
    lgDesktop?: Img
  }
  /** 文章标题 */
  title: string
  /** 文章描述 */
  description?: string
  /** 日期信息（分类·日期·作者） */
  date?: string
  /** 文章链接 */
  link?: string
  /** 卡片宽度比例，默认 full */
  width?: FeaturedBlogPostSpan
}

/**
 * FeaturedBlogPosts 业务组件数据接口
 */
export interface FeaturedBlogPostsData {
  /** 主标题 */
  title?: string
  /** 副标题 */
  subtitle?: string
  /** 博文列表 */
  items: FeaturedBlogPostItem[]
}

export interface FeaturedBlogPostsProps extends React.HTMLAttributes<HTMLDivElement> {
  /** 业务数据 */
  data: FeaturedBlogPostsData
  /** 语义化类名覆盖 */
  classNames?: Partial<Record<FeaturedBlogPostsSemanticName, string>>
  /** 卡片点击回调 */
  onItemClick?: (item: FeaturedBlogPostItem, index: number, event: React.MouseEvent) => void
}

/**
 * 响应式图片类型
 */
type ResponsiveImage = FeaturedBlogPostItem['image']

/**
 * 将响应式图片对象转为 Picture source 格式字符串
 * 格式: "url1 1920, url2 1440, url3 1024, url4 768, url5 390"
 */
const getImageSource = (image: ResponsiveImage): string => {
  const breakpoints: { key: keyof ResponsiveImage; width: number }[] = [
    { key: 'lgDesktop', width: 1920 },
    { key: 'desktop', width: 1440 },
    { key: 'laptop', width: 1024 },
    { key: 'pad', width: 768 },
    { key: 'mobile', width: 390 },
  ]

  const sources = breakpoints
    .filter(({ key }) => image[key]?.url)
    .map(({ key, width }) => `${image[key]!.url} ${width}`)

  // 如果有 sources，返回拼接结果；否则返回第一个有效的 url
  if (sources.length > 0) {
    return sources.join(', ')
  }

  // fallback: 返回任意一个有效的 url
  for (const { key } of breakpoints) {
    if (image[key]?.url) return image[key]!.url
  }

  return ''
}

/**
 * 从响应式图片对象获取 alt 文本
 */
const getImageAlt = (image: ResponsiveImage, fallback: string): string => {
  const keys: (keyof ResponsiveImage)[] = ['desktop', 'laptop', 'pad', 'mobile', 'lgDesktop']
  for (const key of keys) {
    if (image[key]?.alt) return image[key]!.alt!
  }
  return fallback
}

/**
 * 根据 span 获取卡片 grid 列数 (laptop+)
 */
const getSpanClass = (span: FeaturedBlogPostSpan = 'full') => {
  const spanMap: Record<FeaturedBlogPostSpan, string> = {
    full: 'laptop:col-span-3',
    '2/3': 'laptop:col-span-2',
    '1/3': 'laptop:col-span-1',
  }
  return spanMap[span]
}

/**
 * 根据 span 获取卡片布局方向 (laptop+)
 * 1/3: 上下布局，其他: 左右布局
 */
const getLayoutClass = (span: FeaturedBlogPostSpan = 'full') => {
  return span === '1/3' ? '' : 'laptop:flex-row'
}

/**
 * 根据 span 获取图片尺寸样式 (laptop+)
 * full: 左右布局，图片宽度占 2/3
 * 2/3: 左右布局，图片宽度占 1/2
 * 1/3: 上下布局，图片高度填满剩余空间
 */
const getImageSizeClass = (span: FeaturedBlogPostSpan = 'full') => {
  const sizeMap: Record<FeaturedBlogPostSpan, string> = {
    full: 'laptop:h-full laptop:w-2/3',
    '2/3': 'laptop:h-full laptop:w-1/2',
    '1/3': 'laptop:flex-1 h-[200px] lg-desktop:h-[240px]',
  }
  return sizeMap[span]
}

/**
 * FeaturedBlogPosts - 精选博文
 *
 * @description 展示经过筛选的优质文章，支持不同宽度和布局的卡片组合
 */
const FeaturedBlogPostsBase = React.forwardRef<HTMLDivElement, FeaturedBlogPostsProps>(
  ({ className, classNames = {}, data, onItemClick, ...props }, ref) => {
    const { title, subtitle, items } = data

    const handleCardClick = React.useCallback(
      (item: FeaturedBlogPostItem, index: number) => (event: React.MouseEvent) => {
        onItemClick?.(item, index, event)
      },
      [onItemClick]
    )

    return (
      <div
        ref={ref}
        className={cn('blog-posts-root', 'text-info-primary w-full', className, classNames.root)}
        {...props}
      >
        {/* 标题区域 */}
        {(title || subtitle) && (
          <div className={cn('blog-posts-header mb-6', classNames.header)}>
            {title && <Heading as="h2" size={4} html={title} className={cn('blog-posts-title', classNames.title)} />}
            {subtitle && <Text as="p" size={3} className={cn('', classNames?.subtitle)} html={subtitle} />}
          </div>
        )}
        <div className={cn('blog-posts-grid', 'laptop:grid-cols-3 grid grid-cols-1 gap-4', classNames.grid)}>
          {items.map((item, index) => {
            const CardWrapper = item.link ? 'a' : 'div'
            const cardProps = item.link ? { href: item.link } : {}

            return (
              <React.Fragment key={index}>
                <CardWrapper
                  {...cardProps}
                  className={cn(
                    'blog-posts-card rounded-box',
                    'flex cursor-pointer flex-col gap-4',
                    'laptop:h-[336px] desktop:h-[448px] lg-desktop:h-[560px]',
                    'laptop:bg-container-secondary-0 laptop:p-4 desktop:p-6 lg-desktop:p-8',
                    'laptop:overflow-hidden laptop:transition-opacity ',
                    'bg-container-secondary-1',
                    getSpanClass(item.width || 'full'),
                    getLayoutClass(item.width || 'full'),
                    classNames.card
                  )}
                  onClick={handleCardClick(item, index)}
                >
                  {/* 图片 */}
                  <div
                    className={cn(
                      'blog-posts-card-image',
                      'relative w-full overflow-hidden',
                      'h-[200px] shrink-0',
                      getImageSizeClass(item.width || 'full'),
                      classNames.cardImage
                    )}
                  >
                    <Picture
                      source={getImageSource(item.image)}
                      alt={getImageAlt(item.image, item.title)}
                      className="rounded-box h-full object-cover"
                      imgClassName="h-full"
                    />
                  </div>

                  {/* 内容 */}
                  <div
                    className={cn(
                      'blog-posts-card-content',
                      'desktop:gap-6 flex flex-col justify-center gap-4',
                      classNames.cardContent
                    )}
                  >
                    <div className="desktop:gap-2 flex flex-col gap-1">
                      <Heading
                        size={2}
                        as="h6"
                        className={cn('blog-posts-card-title line-clamp-3', classNames.cardTitle)}
                        html={item.title}
                      />

                      {item.description && (
                        <Text
                          className={cn(
                            'blog-posts-card-description desktop:text-[16px] lg-desktop:text-[18px] line-clamp-3 text-[14px]',
                            classNames.cardDescription
                          )}
                          html={item.description}
                        />
                      )}
                    </div>
                    {item.date && (
                      <Text
                        className={cn(
                          'blog-posts-card-meta',
                          'text-info-tertiary desktop:text-[16px] lg-desktop:text-[18px] text-[14px]',
                          classNames.cardMeta
                        )}
                        html={item.date}
                      />
                    )}
                  </div>
                </CardWrapper>

                {/* 分割线 - 仅 mobile/tablet 显示 */}
                {index < items.length - 1 && (
                  <div
                    className={cn(
                      'blog-posts-separator',
                      'bg-lines-primary laptop:hidden h-px w-full',
                      classNames.separator
                    )}
                  />
                )}
              </React.Fragment>
            )
          })}
        </div>
      </div>
    )
  }
)

FeaturedBlogPostsBase.displayName = 'FeaturedBlogPosts'

const FeaturedBlogPosts = withLayout(FeaturedBlogPostsBase)

export default FeaturedBlogPosts
